import { AlertTriangle, ShieldAlert, CheckCircle2, Info, Siren, CalendarCheck, Ban, ArrowRight, BookOpen } from "lucide-react";
import { useEmergencies, openEmergency } from "../lib/emergencies";

function EmergencyChips({ id }) {
  const data = useEmergencies();
  const ids = data?.item_map[id] || [];
  if (!ids.length) return null;
  const byId = Object.fromEntries(data.protocols.map((p) => [p.id, p]));
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2" data-testid={`emergency-chips-${id}`}>
      <span className="text-xs font-bold text-red-700 flex items-center gap-1"><Siren className="w-3.5 h-3.5" /> If a medical emergency occurs:</span>
      {ids.map((pid) => (
        <button key={pid} type="button" onClick={() => openEmergency(pid)} data-testid={`emergency-chip-${id}-${pid}`}
          className="text-xs font-semibold rounded-full border border-red-300 bg-white text-red-700 px-2.5 py-1 hover:bg-red-600 hover:text-white transition-colors">
          {byId[pid]?.chip}
        </button>
      ))}
    </div>
  );
}

export const RISK = {
  green: { label: "ROUTINE", cardBg: "bg-emerald-50", border: "border-emerald-200", badge: "bg-emerald-100 text-emerald-800", icon: "text-emerald-600", bar: "bg-emerald-500", Icon: CheckCircle2 },
  yellow: { label: "MODIFY / CAUTION", cardBg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-100 text-amber-800", icon: "text-amber-600", bar: "bg-amber-500", Icon: AlertTriangle },
  orange: { label: "SPECIALIST APPROVAL", cardBg: "bg-orange-50", border: "border-orange-300", badge: "bg-orange-100 text-orange-800", icon: "text-orange-600", bar: "bg-orange-500", Icon: AlertTriangle },
  red: { label: "EMERGENCY / DEFER", cardBg: "bg-red-50", border: "border-red-200", badge: "bg-red-100 text-red-800", icon: "text-red-600", bar: "bg-red-500", Icon: ShieldAlert },
};

export function RiskBadge({ status, className = "" }) {
  const r = RISK[status] || RISK.green;
  return (
    <span data-testid={`risk-badge-${status}`} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-wide ${r.badge} ${className}`}>
      <r.Icon className="w-3.5 h-3.5" />
      {r.label}
    </span>
  );
}

export function RecommendationCard({ rec }) {
  const r = RISK[rec.status] || RISK.green;
  return (
    <div data-testid={`recommendation-${rec.item_id}`} className={`relative rounded-xl border ${r.border} ${r.cardBg} p-6 overflow-hidden`}>
      <span className={`absolute left-0 top-0 h-full w-1.5 ${r.bar}`} />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{rec.category}</p>
          <h4 className="font-heading text-lg font-semibold text-slate-900 mt-0.5">{rec.item_name}</h4>
        </div>
        <RiskBadge status={rec.status} />
      </div>
      <p className="mt-3 font-semibold text-slate-900">{rec.headline}</p>
      <p className="mt-1.5 text-sm text-slate-700 leading-relaxed">{rec.rationale}</p>
      {rec.modifications?.length > 0 && (
        <ul className="mt-4 space-y-1.5">
          {rec.modifications.map((m, i) => (
            <li key={i} className="flex gap-2 text-sm text-slate-800">
              <Info className={`w-4 h-4 mt-0.5 shrink-0 ${r.icon}`} />
              <span>{m}</span>
            </li>
          ))}
        </ul>
      )}
      <InfoBlock info={rec.info} id={rec.item_id} />
      <Pathways pathways={rec.pathways} id={rec.item_id} />
      <DrugsToAvoid drugs={rec.drugs_to_avoid} id={rec.item_id} />
      <EmergencyChips id={rec.item_id} />
      {rec.references?.length > 0 && (
        <div data-testid={`references-${rec.item_id}`} className="mt-4 text-xs text-slate-500">
          <p className="font-semibold uppercase tracking-wider">References</p>
          <ol className="mt-1 list-decimal pl-4 space-y-0.5 italic">{rec.references.map((x, i) => <li key={i}>{x}</li>)}</ol>
        </div>
      )}
      <p data-testid={`second-opinion-${rec.item_id}`} className="mt-4 pt-3 border-t border-black/5 text-[11px] text-slate-500 flex gap-1.5"><ShieldAlert className="w-3.5 h-3.5 shrink-0" /> {SECOND_OPINION}</p>
    </div>
  );
}

export function InfoBlock({ info, id }) {
  if (!info?.length) return null;
  return (
    <div data-testid={`info-${id}`} className="mt-4 rounded-lg border border-sky-200 bg-white/80 p-4">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sky-800"><BookOpen className="w-4 h-4" /> Why this matters</p>
      <ul className="mt-2 space-y-1.5">{info.map((t, i) => <li key={i} className="text-sm text-slate-700 leading-relaxed">{t}</li>)}</ul>
    </div>
  );
}

function PathBox({ title, items, Icon, tone, testid }) {
  return (
    <div data-testid={testid} className={`rounded-lg border-l-4 p-4 ${tone}`}>
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider"><Icon className="w-4 h-4" /> {title}</p>
      <ul className="mt-2 space-y-2">
        {items.map((s, i) => {
          const step = typeof s === "string" ? { text: s } : s;
          return (
            <li key={i} className="text-sm text-slate-800 leading-relaxed">
              • {step.text}
              {step.ref && <span className="block text-[11px] italic text-slate-500 mt-0.5 pl-3">Ref: {step.ref}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function Pathways({ pathways, id }) {
  if (!pathways) return null;
  return (
    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
      <PathBox testid={`pathway-emergency-${id}`} title="If EMERGENCY — what to do" items={pathways.emergency || []} Icon={Siren} tone="border-red-600 bg-red-100/70 text-red-800" />
      <PathBox testid={`pathway-routine-${id}`} title="If ROUTINE — what to do" items={pathways.routine || []} Icon={CalendarCheck} tone="border-emerald-600 bg-emerald-100/70 text-emerald-800" />
    </div>
  );
}

export function DrugsToAvoid({ drugs, id }) {
  if (!drugs?.length) return null;
  return (
    <div data-testid={`drugs-avoid-${id}`} className="mt-4 rounded-lg border border-slate-200 bg-white overflow-hidden">
      <p className="flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-700 bg-slate-50 border-b border-slate-200"><Ban className="w-4 h-4 text-red-600" /> Medications to avoid → safer alternative</p>
      <ul className="divide-y divide-slate-100">
        {drugs.map((d, i) => (
          <li key={i} className="px-4 py-3 text-sm">
            <div className="flex flex-wrap items-start gap-2">
              <span className="font-semibold text-red-700">{d.avoid}</span>
              <ArrowRight className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
              <span className="font-semibold text-emerald-700">{d.alternative}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{d.reason}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const SECOND_OPINION = "This recommendation is an evidence-based second opinion to assist the dentist. It does not replace your professional clinical judgment or consultation with the patient's physician.";

export function MedicalDisclaimer({ testid = "medical-disclaimer" }) {
  return (
    <div data-testid={testid} className="mt-5 flex gap-3 rounded-xl border border-slate-300 bg-slate-50 p-4">
      <ShieldAlert className="w-5 h-5 text-slate-600 shrink-0 mt-0.5" />
      <p className="text-sm text-slate-700 leading-relaxed"><b>Important — second opinion only.</b> DentiCision provides an evidence-based second opinion to assist the dentist. It does not replace the treating dentist's professional clinical judgment or consultation with the patient's physician. The treating dentist retains full clinical, scientific and legal responsibility. Always verify drugs and doses against current guidelines.</p>
    </div>
  );
}
